import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { WeeklyHero } from "@/components/WeeklyHero";
import { WeeklyTimelineHero } from "@/components/WeeklyTimelineHero";
import { useCreatorBySlug, useCreatorImageUrl, useWeeklyDrops } from "@/lib/firebase";
import { useAuth } from "@/lib/auth";
import { useDevState } from "@/lib/dev-state";
import { useLibraryAccess } from "@/lib/use-library-access";
import { isLibraryOnlyMember } from "@/lib/membership-tier";
import { Play, ShoppingBag } from "lucide-react";

export const Route = createFileRoute("/creator/$slug")({
  head: () => ({
    meta: [
      { title: "Creator — Real Pranks" },
      { name: "description", content: "Watch videos from Real Pranks creators." },
    ],
  }),
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold">Creator not found</h1>
        <Link to="/" className="mt-4 inline-block text-primary hover:underline">
          ← Back to home
        </Link>
      </div>
    </div>
  ),
  component: CreatorPage,
});

function CreatorPage() {
  const { slug } = Route.useParams();
  const { creator, loading } = useCreatorBySlug(slug);
  const bannerUrl = useCreatorImageUrl(creator?.banner);
  const avatarUrl = useCreatorImageUrl(creator?.image);
  const { isSubscriber, subscriptions } = useAuth();
  const dev = useDevState();
  const { access } = useLibraryAccess();
  const isLibraryOnly = isLibraryOnlyMember(subscriptions, {
    enabled: dev.state !== "real" && dev.isSubscribed,
    tier: dev.tier,
  });
  const {
    drops: allDrops,
    current,
    next,
    nextReleaseAt,
    releasePhase,
    scheduleNow,
    loading: dropsLoading,
  } = useWeeklyDrops({ seriesSlug: creator?.slug });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-7xl px-4 py-24 text-sm text-muted-foreground">
          Loading creator...
        </main>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="text-3xl font-bold">Creator not found</h1>
          <Link to="/" className="mt-4 inline-block text-primary hover:underline">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  // Build a 5-week window centered on the current drop, matching the home page.
  const currentIdx = Math.max(
    0,
    current
      ? allDrops.findIndex(
          (d) => d.seriesId === current.seriesId && d.week === current.week,
        )
      : allDrops.findIndex((d) => d.status === "current"),
  );
  const subscriberStart = Math.max(0, currentIdx - 2);
  const subscriberTimeline = allDrops.slice(subscriberStart, subscriberStart + 5);
  const loStart = Math.max(0, currentIdx - 2);
  const loTimeline = allDrops.slice(loStart, loStart + 5);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Banner */}
      <div className="relative h-48 w-full overflow-hidden bg-card sm:h-64 md:h-80">
        {bannerUrl && <img src={bannerUrl} alt="" className="h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
      </div>

      <main className="mx-auto max-w-7xl px-4">
        {/* Avatar overlapping banner */}
        <div className="relative z-10 -mt-16 flex items-end gap-4 sm:-mt-20">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={creator.name}
              className="relative z-10 h-28 w-28 flex-shrink-0 rounded-full border-4 border-background object-cover shadow-xl sm:h-36 sm:w-36"
            />
          ) : (
            <div
              aria-hidden
              className="relative z-10 flex h-28 w-28 flex-shrink-0 items-center justify-center rounded-full border-4 border-background bg-secondary text-3xl font-bold text-muted-foreground shadow-xl sm:h-36 sm:w-36"
            >
              {creator.name.trim().charAt(0).toUpperCase() || "?"}
            </div>
          )}
          <div className="pb-2">
            <h1 className="text-2xl font-extrabold text-foreground sm:text-4xl">{creator.name}</h1>
            {creator.summary && (
              <p className="mt-1 text-sm text-muted-foreground">{creator.summary}</p>
            )}
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
          {/* Sidebar */}
          <aside className="h-fit rounded-xl border border-border bg-card p-2">
            <button className="flex w-full items-center gap-2 rounded-lg bg-secondary px-4 py-3 text-left text-sm font-semibold text-foreground">
              <Play className="h-4 w-4 fill-current" /> Weekly Drops
            </button>
            <Link
              to="/merch"
              className="mt-1 flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              <ShoppingBag className="h-4 w-4" /> Merch
            </Link>
          </aside>

          {/* Drops */}
          <section className="space-y-8">
            {dropsLoading ? (
              <p className="text-muted-foreground">Loading drops...</p>
            ) : allDrops.length === 0 ? (
              <p className="text-muted-foreground">No drops yet.</p>
            ) : isSubscriber && current ? (
              <>
                <WeeklyHero
                  drop={current}
                  nextDrop={next}
                  nextReleaseAt={nextReleaseAt}
                  scheduleNow={scheduleNow}
                  releasePhase={releasePhase}
                  isSubscriber={isSubscriber}
                  access={access}
                  isLibraryOnly={isLibraryOnly}
                />
                {subscriberTimeline.length > 0 && (
                  <WeeklyTimelineHero
                    drops={subscriberTimeline}
                    isSubscriber={isSubscriber}
                    access={access}
                    isLibraryOnly={isLibraryOnly}
                    scheduleNow={scheduleNow}
                    releasePhase={releasePhase}
                  />
                )}
              </>
            ) : (
              loTimeline.length > 0 && (
                <WeeklyTimelineHero
                  drops={loTimeline}
                  isSubscriber={false}
                  access={access}
                  obscureLast
                  scheduleNow={scheduleNow}
                  releasePhase={releasePhase}
                />
              )
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

